import { Alert, LoadingSpinner, QueryErrorAlert } from '@/shared/components';
import { PermissionGuard } from '@/features/auth';
import { SchedulePreviewTable } from '../../SchedulePreviewTable';
import type { SchedulePreviewParams, SchedulePreview } from '../../../types/schedule.types';
import type { AppliedDeposit } from '../../../services/appliedDeposit';
import type { SchedulingMethod } from '@/features/products/types/products.types';
import type { DateKey } from '../../../utils/dateKey';
import { isIllustrativeScheduleMethod } from '../../../utils/illustrativeSchedule';

// UI gating is convenience only; quote-service enforces quote.read on the route.
// eslint-disable-next-line react-refresh/only-export-components
export const SCHEDULE_PREVIEW_PERMISSION = { resource: 'quote', action: 'read' } as const;

interface SchedulePreviewPaneProps {
    selectedMethod: SchedulingMethod | null;
    params: SchedulePreviewParams | null;
    data: SchedulePreview | undefined;
    isLoading: boolean;
    isFetching: boolean;
    error: Error | null;
    currency: string;
    appliedDeposit: AppliedDeposit | undefined;
    isPlaceholderData: boolean;
    showStaleNotice: boolean;
    isDisplayedScheduleRecorded: boolean;
    anchor: DateKey;
}

export function SchedulePreviewPane({
    selectedMethod,
    params,
    data,
    isLoading,
    isFetching,
    error,
    currency,
    appliedDeposit,
    isPlaceholderData,
    showStaleNotice,
    isDisplayedScheduleRecorded,
    anchor,
}: SchedulePreviewPaneProps) {
    return (
        <PermissionGuard
            permission={SCHEDULE_PREVIEW_PERMISSION}
            showFallback
            fallback={
                <div className="p-6 bg-mp-background border border-mp-border rounded-lg text-center">
                    <p className="text-sm text-mp-muted">
                        You do not have permission to preview payment schedules.
                    </p>
                </div>
            }
        >
            {!selectedMethod ? (
                <div
                    data-testid="schedule-preview-empty"
                    className="p-6 bg-mp-background border border-mp-border rounded-lg text-center"
                >
                    <p className="text-sm text-mp-muted">
                        Choose a scheduling method to preview the payment schedule.
                    </p>
                </div>
            ) : error ? (
                <div data-testid="schedule-preview-error">
                    <QueryErrorAlert message={error.message} />
                </div>
            ) : isLoading ? (
                <LoadingSpinner message="Calculating schedule..." className="p-4" />
            ) : // Gate on params as well as data: keepPreviousData holds the previous
            // schedule after the query disables.
            params && data ? (
                <div
                    data-testid="schedule-preview-data"
                    // Top padding is lighter than the sides: the always-mounted
                    // status line below reserves a blank row of its own.
                    className="px-6 pt-4 pb-6 bg-mp-surface border border-mp-border rounded-lg space-y-3"
                >
                    {/*
                     * Region stays mounted so it exists in the a11y tree before content
                     * appears; min-h-4 reserves the line so the table below doesn't jump.
                     */}
                    <p
                        data-testid="schedule-refetching"
                        role="status"
                        className="min-h-4 text-xs text-mp-muted"
                    >
                        {isFetching && 'Updating...'}
                    </p>
                    <SchedulePreviewTable
                        schedule={data.schedule}
                        meta={data.meta}
                        numPayments={data.numPayments}
                        currency={currency}
                        depositRowConfirmed={appliedDeposit?.depositRowConfirmed === true}
                        anchorDate={anchor}
                        showSummaryLines={!isPlaceholderData}
                        collapsibleRows
                        illustrativeDates={isIllustrativeScheduleMethod(selectedMethod?.key)}
                    />
                    {/*
                     * Region stays mounted so it exists in the a11y tree before content
                     * appears; keep the wrapper unstyled so it self-collapses when empty.
                     */}
                    <div data-testid="schedule-selection-stale-region" role="status">
                        {showStaleNotice && (
                            <div data-testid="schedule-selection-stale">
                                <Alert variant="warning" role="note">
                                    The schedule has changed since it was recorded. Review
                                    the updated payments — they are recorded automatically
                                    once the preview finishes.
                                </Alert>
                            </div>
                        )}
                    </div>
                    {isDisplayedScheduleRecorded && (
                        <span data-testid="schedule-selection-confirmed" className="sr-only">
                            Selected — you can proceed to the next step.
                        </span>
                    )}
                </div>
            ) : null}
        </PermissionGuard>
    );
}
